import snakecaseKeys from 'snakecase-keys';
import { restClient } from '../client';
import { BusinessEntity, InsuranceCompany, License, LicenseCollabAgreement, MentalHealthRole, ProviderInsuranceRosterStatusEnum } from '../resources';


export const getProviderLicenses = (providerId: string) => restClient.request<{
  data: License[];
}>(`/api/v1/provider/${providerId}/license`);

export const getProviderLicensesCollabAgreements = (providerId: string) => restClient.request<{
  data: {
    [licenseId: string]: LicenseCollabAgreement[];
  };
}>(`/api/v1/provider/${providerId}/license/collab_agreements`);

export type CreateProviderLicenseInput = {
  expiration: string;
  number: string;
  stateId: string;
  mentalHealthRoleId: string;
};


export enum ProviderInsuranceArchiveReason {
  addedByMistake = 'added_by_mistake',
  noLongerInNetwork = 'no_longer_in_network',
}


export const createProviderLicense = (providerId: string, data: CreateProviderLicenseInput) => restClient.request<{
  data: License;
}>(`/api/v1/provider/${providerId}/license`, {
  method: 'POST',
  data: snakecaseKeys(data),
});

export type CreateProviderLicenseCollabAgreementInput = {
  fileUploadId: string;
  expirationDate: string | null; // "YYYY-MM-DD", null = no expiration (perpetual)
  physicianFirstName: string;
  physicianLastName: string;
  physicianNpi: string;
  physicianEmail: string;
  physicianPhone: string | null;
};

export type CreateProviderLicenseCollabAgreementResponse = {
  data: LicenseCollabAgreement;
};

export const createProviderLicenseCollabAgreement = (providerId: string, licenseId: string, data: CreateProviderLicenseCollabAgreementInput) => restClient.request<CreateProviderLicenseCollabAgreementResponse>(`/api/v1/provider/${providerId}/license/${licenseId}/collab_agreement`, {
  method: 'POST',
  data: snakecaseKeys(data),
});

export type GetCollaborationAgreementDownloadLinkResponse = {
  data: {
    download_link: string;
  };
};

export type PatchProviderLicenseCollabAgreementInput = {
  isReviewed: boolean;
};

export const patchProviderLicenseCollabAgreement = (licenseId: string, collabAgreementId: string, input: PatchProviderLicenseCollabAgreementInput) => restClient.request(`/api/v1/admin/license/${licenseId}/collab_agreement/${collabAgreementId}`, {
  method: 'PATCH',
  data: snakecaseKeys(input),
});

export const getCollborationAgreementDownloadLink = (providerId: string, collabAgreementId: string) => restClient.request<GetCollaborationAgreementDownloadLinkResponse>(`/api/v1/provider/${providerId}/collab_agreement/${collabAgreementId}/download_link`, { method: 'POST' });

export const getProviderMissingPayers = (providerId: string) => restClient.request<{
  data: Record<string, InsuranceCompany[]>;
}>(`/api/v1/provider/${providerId}/missing_payers`);


export type UpdateProviderLicenseInput = {
  expiration: string;
  number: string;
  stateId: string;
  mentalHealthRoleId: string;
};


export const getMentalHealthRoles = () => restClient.request<{
  data: MentalHealthRole[];
}>('/api/v1/mental_health_roles');


export const getProviderInsuranceExistingBusinessEntities = (providerId: string, licenseId: string, insuranceCompanyId: string) => restClient.request<{
  data: BusinessEntity[];
}>(`/api/v1/provider/${providerId}/license/${licenseId}/${insuranceCompanyId}/existing_business_entities`);


export const getProviderInsuranceAvailableBusinessEntities = (providerId: string, licenseId: string, insuranceCompanyId: string) => restClient.request<{
  data: BusinessEntity[];
}>(`/api/v1/provider/${providerId}/license/${licenseId}/${insuranceCompanyId}/available_business_entities`);


export const addProviderInsuranceBusinessEntity = (providerId: string, licenseId: string, insuranceCompanyId: string, businessEntities: {
  businessEntityId: string;
  effectiveDate?: string;
  rosterStatus: ProviderInsuranceRosterStatusEnum | null;
}[]) => restClient.request<{
  data: License;
}>(`/api/v1/provider/${providerId}/license/${licenseId}/${insuranceCompanyId}/business_entity`, {
  method: 'POST',
  data: {
    business_entities: snakecaseKeys(businessEntities)
  },
});


export const removeProviderInsuranceBusinessEntity = (providerId: string, licenseId: string, insuranceCompanyId: string, businessEntityId: string, archiveReason: ProviderInsuranceArchiveReason) => restClient.request<{
  data: License;
}>(`/api/v1/provider/${providerId}/license/${licenseId}/${insuranceCompanyId}/business_entity`, {
  method: 'DELETE',
  data: {
    business_entity_id: businessEntityId,
    archive_reason: archiveReason,
  },
});
